<template>
    <div>
        <!--    Buyflow - Session Timeout Popup    -->
        <AppModal
            v-if="userIsActive === false"
            :skipTrigger="true"
            :override-visibility="true"
            @dismiss="activeClick()"
            modal-type="info">
            <template #contents>
                <h4 class="text-2xl mb-4 text-center">Your session is about to expire.</h4>
                <p class="text-center font-book mb-2">For your security we will automatically end your session in <strong>{{formattedTime}}</strong>.</p>
                <p class="text-center font-book mb-3">If you would like to continue your shopping experience please close this&nbsp;message.</p>
                <button type="button" class="buyflow-button flex mx-auto" @click="activeClick()">
                    Continue Shopping
                </button>
            </template>
        </AppModal>

        <!--    Buyflow - Whole Page    -->
        <div class="desktop:absolute desktop:w-full desktop:top-0" v-if="meta.conversationId && isReady">
            <!--    Buyflow - Top Bar (Logo, Address, Mobile - (PhoneNumber, chat))    -->
            <AppHeader
                :address="addressString"
                @change-address="changeAddress()"
            />

            <!--    Buyflow - Breadcrumbs + Section Heading    -->
            <div class="container print:hidden py-6 desktop:py-8">
                <AppBreadcrumbs
                    v-if="$route.meta.breadcrumbs"
                    @breadcrumb-navigate="routeTo($event)"
                    :breadcrumbs="allBreadcrumbs"
                    :current-route-name="currentRouteName"
                    :section-status="sectionStatus"
                    class="pb-6 desktop:pb-8"
                />
                <h1 class="text-lg desktop:text-2xl text-center text-navy-dark font-bold leading-none">
                    {{ $route.meta.heading }}
                </h1>
            </div>

            <!--    Buyflow - Order Submission Error (typically caused by duplicate orders)    -->
            <div v-if="summaryError"
                class="container mb-24 desktop:mb-28 px-4 desktop:px-0">
                <div class="buyflow-panel border-4 border-yellow-200 bg-yellow-50 text-navy-dark text-center">
                    <div class="font-bold text-lg tablet:text-xl mb-2">
                        We've encountered an issue submitting your order
                    </div>
                    <p class="text-sm">
                        Looks like you are already a current customer. If this is not correct, please try again or call 855-284-3806.
                    </p>
                </div>
            </div>

            <!--    Buyflow - Main Section    -->
            <div class="container mb-24 desktop:mb-28 px-4 desktop:px-0">
                <div class="grid grid-cols-12 gap-4 print:block">
                    <!--    Buyflow - Main Content    -->
                    <div class="col-span-12 desktop:col-span-8" v-if="currentRouteName !== 'buyflow-summary'">
                        <RouterView
                            @update-next-disabled-state="updateNextDisabledState($event)"
                            name="main"
                            class="space-y-4"
                        />
                    </div>
                    <div class="col-span-12 desktop:col-span-12" v-if="currentRouteName === 'buyflow-summary'" >
                        <RouterView
                            @update-next-disabled-state="updateNextDisabledState($event)"
                            :product-metadata="productMetadata"
                            name="main"
                            class="space-y-4"
                        />
                    </div>

                    <!--    Buyflow - Main Sidebar    -->
                    <div class="col-span-12 desktop:col-span-4 print:hidden" v-if="currentRouteName !== 'buyflow-summary'">
                        <!--    Buyflow - Loading Receipt   -->
                        <AppCartLoading
                            v-if="currentRouteName !== 'buyflow-summary' && receiptLoading"
                            ref="AppCartLoading"
                            class="mb-4"
                            :cart-data="cartData"
                            :current-route-name="currentRouteName"
                            :edit-is-hidden="sectionStatus('buyflow_hide', 'edits')"
                            @on-edit-click="routeTo($event)"
                        />
                        <!--    Buyflow - Receipt    -->
                        <AppCart
                            v-else-if="currentRouteName !== 'buyflow-summary' && !receiptLoading"
                            ref="AppCart"
                            class="mb-4"
                            :cart-data="cartData"
                            :current-route-name="currentRouteName"
                            :product-metadata="productMetadata"
                            :edit-is-hidden="sectionStatus('buyflow_hide', 'edits')"
                            @on-edit-click="routeTo($event)"
                        />

                        <!--    Buyflow - Sidebar Content (typically used on the summary page)    -->
                        <RouterView
                            v-if="!summaryError"
                            name="sidebar"
                            class="mb-4"
                        />
                    </div>
                </div>
            </div>

            <!--    Buyflow - Bottom Bar Page Navigation   -->
            <AppPageNav
                v-if="pageNavData"
                v-bind="pageNavData"
                :next-is-allowed="pageNavNextIsAllowed"
                :uuid="$route.meta.uuid"
                :conversation-id="$route.meta.conversationId"
                :phone-number="phoneNumber"
                :section-status="sectionStatus" @skip="routeTo($event)"
                @next="routeTo($event)">
                <!--     Buyflow - Minified Cart (Mobile Use Only)    -->
                <AppMiniCart
                    :cart-data="cartData"
                    :is-visible="miniCart"
                    :product-metadata="productMetadata"
                    class="desktop:hidden"
                    @click.native="scrollToCart()"
                />
            </AppPageNav>
        </div>
        <!--    Buyflow - Abandoned Cart Returning Customer   -->
        <AbandonedCart v-else-if="returningCustomer"
            :product-metadata="productMetadata"
        />
    </div>
</template>

<script>
    import AppHeader from '~/Buyflow/components/AppHeader'
    import AppBreadcrumbs from '~/Buyflow/components/AppBreadcrumbs'
    import AppPageNav from '~/Buyflow/components/AppPageNav'
    import AppCart from '~/Buyflow/components/AppCart'
    import AppCartLoading from '~/Buyflow/components/AppCart/loading'
    import AppModal from '~/Buyflow/components/AppModal'
    import AppMiniCart from '~/Buyflow/components/AppMiniCart'
    import { mapState, mapGetters, mapActions } from 'vuex'
    import { debounce, } from 'debounce'
    import AbandonedCart from "./components/AbandonedCart"
    import CountdownTimer from "../../components/molecules/CountdownTimer"
    import {DateTime as dt} from 'luxon'

    export default {
        name: 'Buyflow',
        components: {
            AbandonedCart,
            AppHeader,
            AppBreadcrumbs,
            AppPageNav,
            AppCart,
            AppCartLoading,
            AppMiniCart,
            AppModal,
            CountdownTimer,
        },
        props: {
            productMetadata: {
                type: String,
                required: false,
                default: "[]",
            },
        },
        data: () => ({
            disableNext: false, // TODO move to store
            returningCustomer: false, // this is the welcome screen for abandoned cart customers
            countdownTime: 0,
            userIsActive: true,
            show: true,
            sessionInterval: null,
        }),
        computed: {
            ...mapGetters({
                sectionStatus: 'Buyflow/sectionStatus',
                salesforceCart: 'Buyflow/tracking/salesforceCart',
                selectedService: 'Buyflow/internet/selectedService',
                formatInstallDate: 'Buyflow/finalize/formatInstallDate',
            }),
            ...mapState({
                meta: state => state.meta,
                isReady: state => state.Buyflow.isReady,
                session: state => state.Buyflow.session,
                ecommerceItem: state => state.ecommerceItem,
                summary: state => state.Buyflow.summary.summary,
                miniCart: state => state.Buyflow.receipt.miniCart,
                cartData: state => state.Buyflow.receipt.cartData,
                summaryError: state => state.Buyflow.summary.summaryError,
                receiptLoading: state => state.Buyflow.receipt.receiptLoading,
            }),

            phoneNumber() {
                return {
                    'fiber-competitive': '855-479-8563',
                    'ohio': '855-528-6528',
                }[this.$store.state.meta.marketType] || '844-432-3610'
            },

            addressString () {
                return this.$store.getters.getCurrentAddressAsString(this)
            },
            currentRouteName () {
                return this.$route.name
            },
            allBreadcrumbs () {
                return this.$router.matcher.getRoutes()
                    .filter(route => {
                        if (route.name.startsWith('buyflow-')) {
                            return route.meta.breadcrumbs
                        }
                        return false
                    })
                    .map(({ name, meta }) => ({
                        name,
                        text: meta.breadcrumbs.text,
                        shortText: meta.breadcrumbs.shortText,
                    }))
            },
            pageNavData () {
                return this.$route.meta.pageNav
            },
            pageNavNextIsAllowed () {
                let res = true

                if (this.$route.name === 'buyflow-tv') {
                    res = this.$store.state.Buyflow.tv.tvOptions.declined || this.$store.getters['Buyflow/tv/hasSelectedAService'] || this.$store.getters['Buyflow/tv/onlyStreaming']
                }

                if (this.$route.name === 'buyflow-voice') {
                    res = this.$store.state.Buyflow.voice.voiceOptions.declined ||
                        this.$store.getters['Buyflow/voice/hasSelected']() ||
                        this.$store.getters['Buyflow/internet/isEquipmentSelected']({ name: 'Customer Owned Modem' })
                }


                if (this.$route.name === 'buyflow-installation') { // aka finalize
                    res = !this.disableNext
                }

                return res
            },
            formattedTime() {
                const seconds = Math.floor(this.countdownTime % 60)?? 0;
                const minutes = Math.floor((this.countdownTime / 60) % 60)?? 0;
                return `${minutes}m ${seconds}s`;
            }
        },
        watch: {
            isReady (val) {
                setTimeout(() => {
                    this.onScroll()
                }, 200)
            },
            cartData (val) {
                if (val) {
                    this.setReceiptLoading(false)
                }
            },
        },
        created () {
            if (this.isValidUUID(this.$route.query.uuid)) {
                if (this.$route.name === 'buyflow-summary') {
                    // enabling bookmarking for the receipt of the buyflow
                    // this.orderSummary()
                } else {
                    console.log('[why does this run?] retrieveusersession running now..')
                    this.returningCustomer = true
                    this.retrieveUserSession(this.$route.query.uuid)
                }

            } else if (this.meta.customerState === 'current' && !this.session.hasCompletedOrder){
                window.location.replace('/')
            } else if (this.$route.name === 'buyflow-summary') {
                // hit the last page of the buyflow
                // this.orderSummary()
            } else if (this.meta.conversationId) {
                this.reload()
                    .catch(error => {
                        // todo resolve errors causing the buyflow to fail
                        // window.location.href = '/'
                    })
            } else {
                window.location.replace('/')
            }
        },
        mounted () {
			document.body.classList.add('bg-navy-light')
            this.setActive() // initializes the first active step

            window.addEventListener('scroll', debounce(this.onScroll, 200))
            window.addEventListener('scroll', debounce(this.setActive, 200))
            this.$el.addEventListener('mousemove', debounce(this.setActive, 200))
            this.$el.addEventListener('mouseleave', debounce(this.setActive, 200))
            this.sessionInterval  = setInterval(this.validateSession, 1000) // validates the session is still active every second

            if (this.$route.name !== 'buyflow-summary') {
                this.sessionKeepAlive = setInterval(this._fetchCart, 300000) // Fetches the cart every 5 minutes
            }
        },
        beforeDestroy() {
            this.destroyListeners()
        },
        methods: {
            ...mapActions({
                reload: 'Buyflow/reload',
                setActive: 'Buyflow/setActive',
                _fetchCart: 'Buyflow/_fetchCart',
                endSession: 'Buyflow/endSession',
                startSession: 'Buyflow/startSession',
                setEcommerceItem: 'setEcommerceItem',
                setCurrentStep: 'Buyflow/setCurrentStep',
                orderSummary: 'Buyflow/summary/orderSummary',
                setSessionStatus:  'Buyflow/setSessionStatus',
                toggleMiniCart: 'Buyflow/receipt/toggleMiniCart',
                retrieveUserSession: 'Buyflow/retrieveUserSession',
                toggleChannelLineup: 'Buyflow/tv/toggleChannelLineup',
                setReceiptLoading: 'Buyflow/receipt/setReceiptLoading',
            }),
            destroyListeners(){
                window.removeEventListener('scroll', debounce(this.onScroll, 200))
                window.removeEventListener('scroll', debounce(this.setActive, 200))
                this.$el.removeEventListener('mousemove', debounce(this.setActive, 200))
                this.$el.removeEventListener('mouseleave', debounce(this.setActive, 200))
                clearInterval(this.sessionInterval)
                clearInterval(this.sessionKeepAlive)
            },
            changeAddress () {
                this.$store.dispatch('toggleCheckAvailabilityModal', {
                    visible: true,
                    routeToBuyflow: true,
                    resetSelectedOffer: true,
                    currentCustomerCheck: false,
                })
            },
            updateNextDisabledState (value) {
                this.disableNext = value
            },

            routeTo (route) { // NOTE double check to make sure query still works like before
                if (route.name === 'buyflow-summary') {
                    clearInterval(this.sessionKeepAlive)
                    this.$router.push({
                        ...route,
                        query: {
                            uuid: this.meta.uuid,
                            cid: this.meta.conversationId,
                        },
                    })
                } else {
                    this.$router.push(route)
                }
            },
            onScroll() {
                if (this.$refs.AppCart) {
                    let { top, bottom, } = this.$refs.AppCart.$el.getBoundingClientRect()
                    this.toggleMiniCart(!(top < window.innerHeight && bottom >= 0)) // do not show mini cart if actual cart is visible
                } else {
                    this.toggleMiniCart(false)
                }
            },
            scrollToCart() {
                if (this.$refs.AppCart) {
                    this.$refs.AppCart.$el.scrollIntoView({ behavior: 'smooth', })
                }
            },
            activeClick() {
                this.userIsActive = true
                this.startSession()
                this.countdownTime = 0
            },
            validateSession() {
                // This code checks the date of the last session reset and the last active time to determine if the
                // session should either be expired or if the user should be warned that the session is about to expire
                if (!this.session.dateTime) return;  // prevents the code that follows from running if the session dateTime hasn't been set yet
                let now = dt.now()
                if (now.diff(dt.fromISO(this.session.dateTime), 'minutes').toObject().minutes >= this.session.expiryWarning && this.userIsActive === true) {
                    this.userIsActive = false // show the activity modal
                }
                if (now.diff(dt.fromISO(this.session.dateTime), 'minutes').toObject().minutes >= this.session.duration) {
                    this.userIsActive = true // hide the activity modal
                    this.endSession()
                }

                if (!this.session.lastActive) return;  // prevents the code that follows from running if the session lastActive hasn't been set yet
                if (now.diff(dt.fromISO(this.session.lastActive), 'minutes').toObject().minutes >= this.session.activeDuration && this.userIsActive !== false) {
                    this.userIsActive = false // show the activity modal
                }
                if (this.userIsActive === false) {
                    // the amount of seconds left until the session expires
                    this.countdownTime = dt.fromISO(this.session.dateTime).plus({ minutes: this.session.duration }).diff(now, 'seconds')?.seconds ?? 0
                }

            }
        }
    }
</script>
